/**
 * Button Component
 * 
 * Versatile button built on MUI Button with multiple variants and states.
 * Provides consistent styling with HAAT brand colors and enhanced features.
 * 
 * @example
 * // Basic
 * <Button>Click Me</Button>
 * 
 * @example
 * // With icon
 * <Button icon={<Save size={16} />}>Save</Button>
 * 
 * @example
 * // Loading state
 * <Button isLoading>Saving...</Button>
 * 
 * @example
 * // Variants
 * <Button variant="primary">Primary</Button>
 * <Button variant="secondary">Secondary</Button>
 * <Button variant="danger">Delete</Button>
 * <Button variant="ghost">Cancel</Button>
 */

import React, { forwardRef } from 'react';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { SxProps, Theme } from '@mui/material/styles';

// =============================================================================
// TYPES
// =============================================================================

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  /** Visual variant */
  variant?: ButtonVariant;
  /** Size variant */
  size?: ButtonSize;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Icon to display at start */
  icon?: React.ReactNode;
  /** Icon to display at end */
  endIcon?: React.ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Custom MUI sx styles */
  sx?: SxProps<Theme>;
  /** Button content */
  children?: React.ReactNode;
}

// =============================================================================
// SIZE MAPPING
// =============================================================================

const sizeConfig: Record<ButtonSize, { muiSize: 'small' | 'medium' | 'large'; sx: SxProps<Theme> }> = {
  sm: {
    muiSize: 'small',
    sx: {
      px: 2,
      py: 0.75,
      fontSize: '0.875rem',
      minHeight: '32px',
    },
  },
  md: {
    muiSize: 'medium',
    sx: {
      px: 2.5,
      py: 1.25,
      fontSize: '0.875rem',
      minHeight: '40px',
    },
  },
  lg: {
    muiSize: 'large',
    sx: {
      px: 3,
      py: 1.5,
      fontSize: '1rem',
      minHeight: '48px',
    },
  },
};

// =============================================================================
// VARIANT STYLES
// =============================================================================

const getVariantStyles = (variant: ButtonVariant): SxProps<Theme> => {
  const styles: Record<ButtonVariant, SxProps<Theme>> = {
    primary: {
      background: 'linear-gradient(135deg, rgb(var(--color-primary-500)) 0%, rgb(var(--color-primary-700)) 100%)',
      color: 'white',
      boxShadow: '0 4px 14px 0 rgba(var(--color-primary-500), 0.25)',
      '&:hover': {
        background: 'linear-gradient(135deg, rgb(var(--color-primary-600)) 0%, rgb(var(--color-primary-800)) 100%)',
        boxShadow: '0 6px 20px 0 rgba(var(--color-primary-500), 0.35)',
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&.Mui-disabled': {
        background: '#d1d5db',
        color: '#9ca3af',
        boxShadow: 'none',
      },
    },
    secondary: {
      backgroundColor: 'white',
      color: '#374151',
      border: '1px solid #e5e7eb',
      '&:hover': {
        backgroundColor: '#f9fafb',
        borderColor: '#d1d5db',
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&.Mui-disabled': {
        backgroundColor: '#f9fafb',
        color: '#9ca3af',
        borderColor: '#e5e7eb',
      },
    },
    danger: {
      background: 'linear-gradient(135deg, #c00035 0%, #c00035 100%)',
      color: 'white',
      boxShadow: '0 4px 14px 0 rgba(239, 68, 68, 0.25)',
      '&:hover': {
        background: 'linear-gradient(135deg, #c00035 0%, #b91c1c 100%)',
        boxShadow: '0 6px 20px 0 rgba(239, 68, 68, 0.35)',
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&.Mui-disabled': {
        background: '#d1d5db',
        color: '#9ca3af',
        boxShadow: 'none',
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: '#4b5563',
      '&:hover': {
        backgroundColor: '#f3f4f6',
      },
      '&.Mui-disabled': {
        color: '#9ca3af',
      },
    },
  };

  return styles[variant];
};

// =============================================================================
// COMPONENT
// =============================================================================

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      icon,
      endIcon,
      fullWidth = false,
      disabled,
      className,
      sx,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    // =========================================================================
    // COMPUTED VALUES
    // =========================================================================

    const sizeStyles = sizeConfig[size];
    const variantStyles = getVariantStyles(variant);
    const isDisabled = disabled || isLoading;

    // =========================================================================
    // BUILD STYLES
    // =========================================================================

    const buttonStyles = {
      // Base styles
      borderRadius: '12px',
      fontWeight: 600,
      textTransform: 'none' as const,
      transition: 'all 0.2s ease-in-out',
      gap: 1,

      // Size styles
      ...(sizeStyles.sx as object),

      // Variant styles
      ...(variantStyles as object),

      // Focus styles
      '&:focus-visible': {
        outline: 'none',
        boxShadow: variant === 'primary'
          ? '0 0 0 3px rgba(var(--color-primary-500), 0.3)'
          : '0 0 0 3px rgba(156, 163, 175, 0.3)',
      },

      // Custom sx override
      ...(sx as object),
    };

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
      <MuiButton
        ref={ref}
        type={type}
        disabled={isDisabled}
        fullWidth={fullWidth}
        className={className}
        sx={buttonStyles}
        disableElevation
        disableRipple={false}
        {...props}
      >
        {/* Loading spinner */}
        {isLoading && (
          <CircularProgress
            size={16}
            sx={{
              color: variant === 'secondary' || variant === 'ghost' ? '#6b7280' : 'inherit',
            }}
          />
        )}

        {/* Start icon (hidden when loading) */}
        {!isLoading && icon && (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {icon}
          </span>
        )}

        {/* Button text */}
        {children}

        {/* End icon */}
        {endIcon && !isLoading && (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {endIcon}
          </span>
        )}
      </MuiButton>
    );
  }
);

// Display name for debugging
Button.displayName = 'Button';

export default Button;
